import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from '@auth0/auth0-react';

import ApiMethods from "./ApiMethods";

const invalidateProjectQueries = (queryClient) => {
    return queryClient.invalidateQueries({
        predicate: (query) => query.queryKey?.[0]?.includes('/projects')
    });
}

export const useCreateProject = () => {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ organizationId, projectName }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.post(
                `v1/organizations/${organizationId}/projects`,
                accessToken,
                { data: {
                    type: "project",
                    attributes: { name: projectName }
                }}
            );
        },
        onSuccess: () => invalidateProjectQueries(queryClient),
    });
}

export const useUpdateProject = () => {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ projectId, attributes }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/projects/${projectId}`,
                accessToken,
                { data: {
                    type: "project",
                    id: projectId,
                    attributes
                }}
            );
        },
        onSuccess: () => invalidateProjectQueries(queryClient),
    });
}

export const useArchiveProject = () => {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ projectId }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/projects/${projectId}`,
                accessToken,
                { data: {
                    type: "project",
                    id: projectId,
                    attributes: { is_active: false }
                }}
            );
        },
        onSuccess: () => invalidateProjectQueries(queryClient),
    });
}

export const useRestoreProject = () => {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ projectId }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/projects/${projectId}`,
                accessToken,
                { data: {
                    type: "project",
                    id: projectId,
                    attributes: { is_active: true }
                }}
            );
        },
        onSuccess: () => invalidateProjectQueries(queryClient),
    });
}

export const useMoveDatasetToProject = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ datasetId, destProjectId }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/sequencing_runs/${datasetId}`,
                accessToken,
                { data: {
                    type: "sequencing_run",
                    id: datasetId,
                    attributes: { project_id: destProjectId }
                }}
            );
        },
    });
}

export const useCreateSample = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ runId, sampleType, sampleName, file }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.post(
                `v1/sequencing_runs/${runId}/lib_seqs`,
                accessToken,
                { data: {
                    type: "lib_seq",
                    attributes: {
                        sample_type: sampleType,
                        sample_name: sampleName,
                        sample_id: sampleName,
                        library_id: file?.lastModified || Date.now(),
                    }
                }}
            );
        },
    });
}

export const useCreateConcatenatedFastqgz = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ libSeqId, queryParams }) => {
            const accessToken = await getAccessTokenSilently();
            const { extraFields } = queryParams;

            let url = `v1/lib_seqs/${libSeqId}/concatenated_fastqgzs`;
            if (extraFields) url += `?extra_fields=${extraFields.join(",")}`;

            return ApiMethods.post(
                url,
                accessToken,
                { data: {
                    type: "concatenated_fastqgz",
                    attributes: {}
                }}
            );
        },
    });
}

export const useCreateNCBIConcatenatedFastqgz = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ projectId, ncbiSrr }) => {
            const accessToken = await getAccessTokenSilently();

            return ApiMethods.apiRequestRaw(
                'POST',
                `v1/projects/${projectId}/concatenated_fastqgzs`,
                accessToken,
                {
                    data: {
                        type: "concatenated_fastqgz",
                        attributes: {
                            ncbi_srr: ncbiSrr
                        }
                    }
                }
            );
        },
    });
}

export const useCreateSequencingRun = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ projectId, attributes }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.post(
                `v1/projects/${projectId}/sequencing_runs`,
                accessToken,
                {
                    data: {
                        type: "sequencing_run",
                        attributes
                    }
                }
            );
        },
    });
}

export const useUpdateSequencingRun = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ datasetId, attributes }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/sequencing_runs/${datasetId}`,
                accessToken,
                { data: {
                    type: "sequencing_run",
                    id: datasetId,
                    attributes
                }}
            );
        },
    });
}

export const useRecordDatasetViewed = () => {
    const { user, getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ datasetId }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/sequencing_runs/${datasetId}/viewed`,
                accessToken
            );
        },
        onSuccess: () => {
            if (!user?.sub) return;
            queryClient.invalidateQueries({ queryKey: [`v1/users/${user.sub}`] });
        },
    });
}

export const useTriggerAnalysis = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ fastqId, allowLowQuality = false }) => {
            const accessToken = await getAccessTokenSilently();

            const attributes = {
                remove_human_dna: true
            };

            attributes.generate_amr_predictions = true;
            attributes.rapid_turnaround = true;
            attributes.generate_amr_finder_plus_res_genes = true;
            attributes.detect_adapters = true;

            if (allowLowQuality) {
                attributes.allow_low_quality = true;
            }

            return ApiMethods.put(
                `v1/concatenated_fastqgzs/${fastqId}/analyze`,
                accessToken,
                {
                    data: {
                        type: "analyze",
                        attributes
                    }
                }
            );
        },
    });
}

export const useUpdateUser = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ userId, attributes }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/users/${userId}`,
                accessToken,
                { data: {
                    type: "user",
                    id: userId,
                    attributes: attributes
                }}
            );
        },
    });
}

export const useUpdateSample = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ sampleId, sampleName, sampleType }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/lib_seqs/${sampleId}`,
                accessToken,
                { data: {
                    type: "lib_seq",
                    id: sampleId,
                    attributes: {
                        sample_name: sampleName,
                        sample_type: sampleType
                    }
                }}
            );
        },
    });
}

export const useRecordSampleViewed = () => {
    const { user, getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ sampleId }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/lib_seqs/${sampleId}/viewed`,
                accessToken
            );
        },
        onSuccess: () => {
            if (!user?.sub) return;
            queryClient.invalidateQueries({ queryKey: [`v1/users/${user.sub}`] });
        },
    });
}

export const useMoveSampleToDataset = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ sampleId, datasetId }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/lib_seqs/${sampleId}`,
                accessToken,
                { data: {
                    type: "lib_seq",
                    id: sampleId,
                    attributes: { run_id: datasetId }
                }}
            );
        },
    });
}
